import * as Sentry from '@sentry/react';
import {
  ActionCodeSettings,
  Auth,
  User as FirebaseUser,
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  SAMLAuthProvider,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { User } from '@superfunnel/types/user';
import { usePersistedJsonState } from '@superfunnel/react-hooks/usePersistedState';

import { auth, db } from '../Firebase';
import { getSSOAuthProvider as getSSOAuthProviderApi } from '../services/AccountsService';
import { sessionManager } from '../services/SessionService';
import { cleanupNotificationRegistration } from '../services/notificationService';
import { emptyConverter } from '../utils';
import { updateHotjar } from '../utils/hotjar';
import { identifyUser } from '../utils/identify-user';
import { mixpanel } from '../utils/mixpanel';
import { safeLocalStorage } from '../utils/local-storage';
import { recordDashboardActivity } from '../utils/record-activity';

interface AuthContextProps {
  user?: User | null;
  firebaseUser?: FirebaseUser;
  isAuthenticated: boolean;
  isAllowed: boolean;
  isAllowedLoading: boolean;
  isSupportAdmin: boolean;
  logout: () => Promise<void>;
  loginWithGoogle: () => Promise<any>;
  loginWithMicrosoft: () => Promise<any>;
  loginWithEmailLink: (auth: Auth, email: string, href: string) => Promise<any>;
  sendLoginLinkToEmail: (email: string) => Promise<any>;
  loginWithSAML: (providerId: string) => Promise<any>;
  getSSOAuthProvider: (email: string) => Promise<any>;
}

export const AuthContext = createContext<AuthContextProps>({
  logout: async () => {},
  loginWithGoogle: async () => {},
  loginWithMicrosoft: async () => {},
  loginWithEmailLink: async () => {},
  sendLoginLinkToEmail: async () => {},
  loginWithSAML: async () => {},
  getSSOAuthProvider: async () => {},
  isAuthenticated: false,
  isAllowed: false,
  isAllowedLoading: false,
  isSupportAdmin: false,
});

export function AuthContextProvider(props: any) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser>();
  const [user, setUser] = usePersistedJsonState<User | undefined | null>(
    'user',
    undefined,
    safeLocalStorage,
  );
  // Persisted like `isAuthenticated` and `user`: starting from `undefined` on
  // every refresh made `isAllowedLoading` flip false -> true -> false as auth
  // resolved, which unmounted the whole app for a few hundred milliseconds
  // after it had already rendered.
  const [allowedUser, setAllowedUser] = usePersistedJsonState<
    boolean | undefined
  >('allowedUser', undefined, safeLocalStorage);
  const [authResolved, setAuthResolved] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = usePersistedJsonState<boolean>(
    'isAuthenticated',
    undefined,
    safeLocalStorage,
  );

  const isSupportAdmin = useMemo(() => user?.isSupportAdmin ?? false, [user]);

  const isAllowed = useMemo(() => {
    if (!firebaseUser) return true;
    if (allowedUser === false) return false;

    return true;
  }, [firebaseUser, allowedUser]);

  // True while we are still resolving whether the authenticated user is
  // allowed to access the app (i.e. listed in the `allowedUsers` collection).
  const isAllowedLoading = useMemo(
    () => !!firebaseUser && allowedUser === undefined,
    [firebaseUser, allowedUser],
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setAuthResolved(true);
      setFirebaseUser(user || undefined);
      setIsAuthenticated(!!user);
      identifyUser(user || undefined);
      updateHotjar(user);

      // Push user ID to GTM dataLayer for GA4 User-ID tracking
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ user_id: user?.uid ?? null });

      // Record dashboard activity for retention metrics (fire-and-forget,
      // throttled to once per day per device inside the helper).
      if (user) {
        void recordDashboardActivity(user.uid);
      }

      // Create session after successful authentication
      if (user) {
        try {
          await sessionManager.createSession();
        } catch (error) {
          console.error('Failed to create session after login:', error);
          // Don't throw here as we don't want to break the login flow
        }
      } else {
        // Clear session on logout
        sessionManager.clearSession();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [setIsAuthenticated]);

  useEffect(() => {
    Sentry.setUser(
      firebaseUser
        ? {
            id: firebaseUser.uid,
            ...(firebaseUser.email ? { email: firebaseUser.email } : {}),
          }
        : null,
    );
  }, [firebaseUser]);

  useEffect(() => {
    if (!firebaseUser) {
      setUser(undefined);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'users', firebaseUser.uid).withConverter(emptyConverter<User>()),
      async (user) => {
        setUser(user.data() || null);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [firebaseUser, setUser]);

  useEffect(() => {
    if (!firebaseUser) {
      if (authResolved) {
        setAllowedUser(undefined);
      }
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'allowedUsers', firebaseUser.email ?? 'nothing'),
      (allowedUser) => {
        setAllowedUser(allowedUser.exists());
      },
    );

    return () => {
      unsubscribe();
    };
  }, [firebaseUser, authResolved, setAllowedUser]);

  const loginWithGoogle = (): Promise<any> => {
    const provider = new GoogleAuthProvider();
    auth.useDeviceLanguage();
    return signInWithPopup(auth, provider);
  };

  const loginWithMicrosoft = (): Promise<any> => {
    const provider = new OAuthProvider('microsoft.com');
    provider.setCustomParameters({
      // Force re-consent.
      prompt: 'consent',
      // Target specific email with login hint.
      // login_hint: 'user@firstadd.onmicrosoft.com'
    });
    provider.addScope('openid');
    provider.addScope('email');
    auth.useDeviceLanguage();
    return signInWithPopup(auth, provider);
  };

  const loginWithSAML = (providerId: string): Promise<any> => {
    const _provider = new SAMLAuthProvider(providerId);
    auth.useDeviceLanguage();

    return signInWithPopup(auth, _provider).then((result) => {
      console.log('result', result);
      return result;
    });
  };

  const loginWithEmailLink = (
    auth: Auth,
    email: string,
    href: string,
  ): Promise<any> => {
    return signInWithEmailLink(auth, email, href).then((result) => {
      // Clear email from storage.
      window.localStorage.removeItem('emailForSignIn');
      return result;
    });
  };

  const sendLoginLinkToEmail = (email: string): Promise<any> => {
    const auth = getAuth();
    const settings: ActionCodeSettings = {
      url: 'https://app.superfunnel.ai/login',
      handleCodeInApp: true,
    };
    return sendSignInLinkToEmail(auth, email, settings);
  };

  const getSSOAuthProvider = async (email: string): Promise<any> => {
    return getSSOAuthProviderApi(email);
  };

  const logout = async () => {
    if (firebaseUser) {
      await cleanupNotificationRegistration(firebaseUser.uid);
    }
    await signOut(auth);
    setUser(undefined);
    setFirebaseUser(undefined);
    setIsAuthenticated(false);
    sessionManager.clearSession();
    mixpanel.reset();
  };

  return (
    <AuthContext.Provider
      value={{
        user: user,
        firebaseUser,
        isAuthenticated,
        isAllowed,
        isAllowedLoading,
        isSupportAdmin: isSupportAdmin,
        loginWithGoogle,
        loginWithMicrosoft,
        loginWithEmailLink,
        loginWithSAML,
        sendLoginLinkToEmail,
        logout,
        getSSOAuthProvider,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  return useContext(AuthContext);
}
